import React, { useEffect, useState } from 'react';
import { apiService } from '../../services/apiService';
import './SuggererLotModal.css';

interface SuggererLotModalProps {
  isOpen: boolean;
  onClose: () => void;
  // Notifie le parent qu'une suggestion vient d'etre enregistree (ex. toast/refresh).
  onSuccess?: () => void;
}

/**
 * Modale de suggestion d'un nouveau lot technique (bouton "+ Autres" du tableau
 * d'estimation des besoins). L'acheteur exprime un besoin pour un lot absent du
 * catalogue ; la suggestion part en POST et sera traitee cote BO.
 */
const SuggererLotModal: React.FC<SuggererLotModalProps> = ({ isOpen, onClose, onSuccess }) => {
  const [nomLot, setNomLot] = useState('');
  const [justification, setJustification] = useState('');
  const [montant, setMontant] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  // Verrouille le scroll de la page derriere tant que la modale est ouverte.
  useEffect(() => {
    if (!isOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, [isOpen]);

  if (!isOpen) return null;

  const reset = () => {
    setNomLot('');
    setJustification('');
    setMontant('');
    setError(null);
    setDone(false);
    setSubmitting(false);
  };

  const handleClose = () => {
    if (submitting) return;
    reset();
    onClose();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const nom = nomLot.trim();
    if (!nom) {
      setError('Le nom du lot souhaité est obligatoire.');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      await apiService.suggererLot({
        nom_lot: nom,
        justification: justification.trim() || undefined,
        montant_estime: montant.trim() ? parseFloat(montant) : null,
      });
      setSubmitting(false);
      setDone(true);
      onSuccess?.();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Échec de l’envoi de la suggestion.');
      setSubmitting(false);
    }
  };

  return (
    <div className="suggerer-lot-backdrop" onClick={handleClose}>
      <div className="suggerer-lot-modal" onClick={(e) => e.stopPropagation()}>
        <button className="suggerer-lot-close" onClick={handleClose} aria-label="Fermer" type="button">✕</button>

        <h2 className="suggerer-lot-title">Suggérer un lot</h2>

        {done ? (
          <div className="suggerer-lot-success">
            <p>Votre suggestion a bien été envoyée. Elle sera étudiée par Centralis.</p>
            <button type="button" className="suggerer-lot-btn-primary" onClick={handleClose}>Fermer</button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="suggerer-lot-form">
            <p className="suggerer-lot-intro">
              Un besoin sur un lot absent du catalogue ? Décrivez-le, il sera transmis à Centralis.
            </p>

            <div className="suggerer-lot-field">
              <label htmlFor="suggerer-lot-nom">Nom du lot souhaité <span className="suggerer-lot-required">*</span></label>
              <input
                id="suggerer-lot-nom"
                type="text"
                value={nomLot}
                onChange={(e) => setNomLot(e.target.value)}
                maxLength={255}
                placeholder="Ex. Maintenance des espaces verts"
                autoFocus
              />
            </div>

            <div className="suggerer-lot-field">
              <label htmlFor="suggerer-lot-justif">Justification / description</label>
              <textarea
                id="suggerer-lot-justif"
                value={justification}
                onChange={(e) => setJustification(e.target.value)}
                rows={4}
                maxLength={5000}
                placeholder="Pourquoi ce besoin, quel type d’achat…"
              />
            </div>

            <div className="suggerer-lot-field">
              <label htmlFor="suggerer-lot-montant">Montant estimé annuel</label>
              <div className="suggerer-lot-input-euro">
                <input
                  id="suggerer-lot-montant"
                  type="text"
                  inputMode="numeric"
                  value={montant ? Number(montant).toLocaleString('fr-FR') : ''}
                  onChange={(e) => {
                    const digits = e.target.value.replace(/[^\d]/g, '');
                    // Clamp a 1 Md€ a la saisie, coherent avec la borne backend.
                    setMontant(digits && Number(digits) > 1000000000 ? '1000000000' : digits);
                  }}
                  placeholder="0"
                />
                <span className="suggerer-lot-euro" aria-hidden="true">€</span>
              </div>
            </div>

            {error && <p className="suggerer-lot-error">{error}</p>}

            <div className="suggerer-lot-actions">
              <button type="button" className="suggerer-lot-btn-secondary" onClick={handleClose} disabled={submitting}>
                Annuler
              </button>
              <button type="submit" className="suggerer-lot-btn-primary" disabled={submitting}>
                {submitting ? 'Envoi…' : 'Envoyer la suggestion'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default SuggererLotModal;
